import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {elementIdSchema} from '../../schemas';

const toolName = 'set-form-settings' as const;

const formStateSchema = z
  .union([z.literal('normal'), z.literal('success'), z.literal('error')])
  .describe(
    'The current state of the form: normal (default state), success (after successful submission), or error (after failed submission).'
  );

const formMethodSchema = z
  .union([z.literal('get'), z.literal('post')])
  .describe(
    'The HTTP method for form submission: get (data in URL parameters) or post (data in request body).'
  );

const formSettingsSchema = z
  .object({
    state: formStateSchema.optional(),
    name: z
      .string()
      .optional()
      .describe('The name of the form for identification and processing.'),
    redirect: z
      .string()
      .optional()
      .describe('The URL to redirect to after form submission.'),
    action: z
      .string()
      .optional()
      .describe('The URL where the form data will be submitted.'),
    method: formMethodSchema.optional(),
  })
  .describe(
    'Partial form settings to update. Only provided fields will be modified.'
  );

const argumentZod = z.object({
  elementId: elementIdSchema.describe(
    'The ID of the Form element to set settings on. Must be a FormForm or FormWrapper element.'
  ),
  settings: formSettingsSchema,
});

export const setFormSettingsTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Set form settings',
  prettyErrors: {
    fallback: 'Unable to set form settings.',
    patterns: [
      {
        match: 'not found',
        message: 'Element not found.',
      },
      {
        match: 'not a form',
        message: 'This element is not a form.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Set comprehensive settings for a form element. Updates form configuration
      including state, name, redirect URL, action URL, and HTTP method. Only provided
      settings will be modified (partial update). Form states control visual feedback,
      redirect URLs handle post-submission navigation, action URLs define submission
      endpoints, and methods determine how data is sent. Only works with Form elements
      (FormForm or FormWrapper types). Returns null when settings are successfully
      applied. Essential for complete form configuration and integration.`,
    inputSchema: argumentZod,
  },
  type: 'designer',
  confirmBeforeExecuting: true, // Modifies form behavior and submission, requires confirmation
  timeout: 45000, // 45 seconds for setting complex form settings
});
